import { capitalize, cn } from "@repo/shared/utils";
import { DebouncedInput } from "./DebouncedInput";
import type { Column } from "@tanstack/react-table";
import { useMemo } from "react";

export const standardStyles =
  /*@tw*/ "w-36 flex text-form-foreground h-6 rounded-lg border border-input bg-white px-3 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

export const Filter = <T,>({ column }: { column: Column<T, unknown> }) => {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant } = column.columnDef.meta ?? {};

  switch (filterVariant) {
    case "range":
      return <RangeFilter<T> column={column} />;

    case "selectStatus":
      return <StatusFilter<T> column={column} />;

    case "selectReviewStatus":
      return <ReviewStatusFilter<T> column={column} />;

    case "date":
      return <DateFilter<T> column={column} />;

    case "customSelect":
      return <CustomSelectFilter<T> column={column} />;

    case "customBoolean":
      return <CustomBooleanFilter<T> column={column} />;

    case "select":
      return <SelectFilter<T> column={column} />;

    case "approver":
      return <ApproverFilter<T> column={column} />;

    case "usedBy":
      return <UsedByFilter<T> column={column} />;
  }

  return (
    <DebouncedInput
      className={standardStyles}
      onChange={(value) => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? "") as string}
    />
    // See faceted column filters example for datalist search suggestions
  );
};

// filter by date range
export const DateFilter = <T,>({ column }: { column: Column<T, unknown> }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <div>
      <div className="flex flex-col items-center">
        <DebouncedInput
          max={
            (columnFilterValue as [string, string] | undefined)
              ? (columnFilterValue as [string, string])[1]
              : undefined
          }
          type="date"
          value={(columnFilterValue as [string, string])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [string, string]) => {
              if ((value === "" || !value) && (old?.[1] === "" || !old?.[1])) {
                return "";
              }
              return [value, old?.[1]];
            })
          }
          placeholder={`Start`}
          className={standardStyles}
        />
        <span>to</span>
        <DebouncedInput
          min={
            (columnFilterValue as [string, string] | undefined)
              ? (columnFilterValue as [string, string])[0]
              : undefined
          }
          type="date"
          value={(columnFilterValue as [string, string])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [string, string]) => {
              if ((value === "" || !value) && (old?.[0] === "" || !old?.[0])) {
                return "";
              }
              return [old?.[0], value];
            })
          }
          placeholder={`End`}
          className={standardStyles}
        />
      </div>
    </div>
  );
};

// filter by numeric value range
export const RangeFilter = <T,>({ column }: { column: Column<T, unknown> }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className={standardStyles}
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className={standardStyles}
        />
      </div>
      <div className="h-1" />
    </div>
  );
};

// filter by status: "draft" | "public" | "private"
export const StatusFilter = <T,>({
  column,
}: {
  column: Column<T, unknown>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <select
      className={standardStyles}
      onChange={(e) => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      <option value="">All</option>
      <option value="draft">Draft</option>
      <option value="public">Public</option>
      <option value="private">Private</option>
    </select>
  );
};

// filter by review status: "pending" | ""
export const ReviewStatusFilter = <T,>({
  column,
}: {
  column: Column<T, unknown>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <select
      className={standardStyles}
      onChange={(e) => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      <option value="">All</option>
      <option value="pending">Pending</option>
      <option value="approved">Approved</option>
      <option value="deferred">Deferred</option>
      <option value="rejected">Rejected</option>
      <option value="resolved">Resolved (Approved / Rejected)</option>
      <option value="unresolved">Unresolved (Pending / Deferred)</option>
      <option value="n/a">N/A</option>
    </select>
  );
};

// filter by a custom set of select options
export const CustomSelectFilter = <T,>({
  column,
}: {
  column: Column<T, unknown>;
}) => {
  const columnFilterValue = column.getFilterValue();
  const { customSelectOptions } = column.columnDef.meta ?? {};

  return (
    <select
      className={standardStyles}
      onChange={(e) => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      <option value="">All</option>
      {customSelectOptions?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// filter by boolean field with custom true / false labels
export const CustomBooleanFilter = <T,>({
  column,
}: {
  column: Column<T, unknown>;
}) => {
  const columnFilterValue = column.getFilterValue();
  const { customBooleanOptions } = column.columnDef.meta ?? {};

  return (
    <select
      className={standardStyles}
      onChange={(e) =>
        e.target.value === ""
          ? column.setFilterValue(e.target.value)
          : column.setFilterValue(e.target.value === "true")
      }
      value={columnFilterValue?.toString()}
    >
      <option value="">All</option>
      <option value="true">{customBooleanOptions?.trueLabel}</option>
      <option value="false">{customBooleanOptions?.falseLabel}</option>
    </select>
  );
};

// filter by select dropdown populated from existing column data
export const SelectFilter = <T,>({
  column,
}: {
  column: Column<T, unknown>;
}) => {
  const columnFilterValue = column.getFilterValue();
  const sortedUniqueValues = useMemo(
    () =>
      Array.from(column.getFacetedUniqueValues().keys()).sort().slice(0, 5000),
    [column]
  );

  return (
    <select
      className={standardStyles}
      onChange={(e) => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      <option value="">All</option>
      {sortedUniqueValues
        .filter((val) => (val ? true : false))
        .map((value) => (
          // dynamically generated select options from faceted values feature
          <option value={value} key={value}>
            {capitalize(value)}
          </option>
        ))}
    </select>
  );
};

export type ApproverFilterValue =
  | {
      type?: "user" | "team" | "none" | "";
      name?: string;
    }
  | undefined;

export const ApproverFilter = <T,>({
  column,
}: {
  column: Column<T, unknown>;
}) => {
  const columnFilterValue = column.getFilterValue() as ApproverFilterValue;

  // reduce object to undefined when empty (allows resetting the filter without clearing)
  const simplify = (value: ApproverFilterValue): ApproverFilterValue => {
    if (!value?.name && !value?.type) return undefined;
    return value;
  };

  return (
    <div>
      <div className="flex flex-col">
        <span className="mb-2">Type:</span>
        <select
          className={cn(standardStyles, "w-46")}
          onChange={(evt) =>
            column.setFilterValue(
              simplify({
                ...columnFilterValue,
                type: evt.target.value as "user" | "team" | "none" | "",
              })
            )
          }
          value={columnFilterValue?.type ?? ""}
        >
          <option value="">All</option>
          <option value="user">User</option>
          <option value="team">Team</option>
          <option value="none">No Approval Required</option>
        </select>
        {columnFilterValue?.type !== "none" && (
          <>
            <span className="mt-2">Search:</span>
            <DebouncedInput
              className={cn(standardStyles, "w-46")}
              onChange={(name) =>
                column.setFilterValue(
                  simplify({ ...columnFilterValue, name: name?.toString() })
                )
              }
              value={columnFilterValue?.name ?? ""}
            />
          </>
        )}
      </div>
    </div>
  );
};

type UsedByMode = "name" | "none" | "some";

export type UsedByFilterValue =
  | {
      mode?: UsedByMode;
      name?: string;
    }
  | undefined;

export const UsedByFilter = <T,>({
  column,
}: {
  column: Column<T, unknown>;
}) => {
  const columnFilterValue = column.getFilterValue() as UsedByFilterValue;

  const mode = columnFilterValue?.mode ?? "name";
  const name = columnFilterValue?.name ?? "";

  const simplify = (value: UsedByFilterValue): UsedByFilterValue => {
    if ((!value?.mode || value.mode === "name") && !value?.name) {
      return undefined;
    }
    return value;
  };

  return (
    <div>
      <div className="flex flex-col">
        <select
          className={cn(standardStyles, "w-full mb-2")}
          value={mode}
          onChange={(evt) =>
            column.setFilterValue(
              simplify({
                ...columnFilterValue,
                mode: evt.target.value as UsedByMode,
              })
            )
          }
        >
          <option value="name">Search by text</option>
          <option value="none">No entries</option>
          <option value="some">At least one entry</option>
        </select>
        {mode === "name" && (
          <div className="flex flex-row items-center">
            <span className="mt-1.5 mr-2">Name:</span>
            <DebouncedInput
              className={cn(standardStyles, "shrink-0")}
              onChange={(name) =>
                column.setFilterValue(
                  simplify({ ...columnFilterValue, name: name?.toString() })
                )
              }
              value={name}
            />
          </div>
        )}
      </div>
    </div>
  );
};
